/**
 * Phone formatting utilities.
 * Display format: +1 (855) 359-4257  |  (855) 359-4257
 */

export function formatPhoneDisplay(raw: string | null | undefined): string {
  if (!raw) return '—'
  // Check for country code prefix like "+1 ..."
  const codeMatch = raw.match(/^(\+\d+)\s+(.*)$/)
  if (codeMatch) {
    const code = codeMatch[1] ?? ''
    const digits = (codeMatch[2] ?? '').replace(/\D/g, '').slice(0, 10)
    if (digits.length < 10) return raw
    return `${code} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  const digits = raw.replace(/\D/g, '')
  if (digits.length < 10) return raw
  const main = digits.slice(0, 10)
  return `(${main.slice(0, 3)}) ${main.slice(3, 6)}-${main.slice(6)}`
}

/** Format a phone number as the user types — returns (XXX) XXX-XXXX. */
export function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/** Combine a country code and local phone into a single stored string. */
export function combinePhoneCode(phone: string, code: string): string {
  const phoneTrimmed = phone.trim()
  const codeTrimmed = code.trim()
  if (!phoneTrimmed) return ''
  if (codeTrimmed) return `${codeTrimmed} ${phoneTrimmed}`
  return phoneTrimmed
}

/** Split a stored phone string into its country code and local number parts. */
export function splitPhoneCode(raw: string | null | undefined): { phone: string; code: string } {
  if (!raw) return { phone: '', code: '+1' }
  const match = raw.match(/^(\+\d+)\s+(.*)$/)
  if (match) return { code: match[1] ?? '+1', phone: formatPhoneInput(match[2] ?? '') }
  return { phone: formatPhoneInput(raw), code: '+1' }
}

/** Returns true if the value is a valid US phone (10 main digits). */
export function isValidPhone(value: string): boolean {
  return value.replace(/\D/g, '').length === 10
}

/** Returns true if the value is a valid fax number (10 or 11 digits for +1 numbers). */
export function isValidFax(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  return digits.length === 10 || (digits.length === 11 && digits[0] === '1')
}
